// core
import React, { useState, useEffect, useRef } from "react";

// styles
import cx from "classnames";
import styles from "./ContentSlider.module.scss";

// other
import ContentSliderArrow from "./ContentSliderArrow";

interface IContentSliderProps {
    className?: string;
    // The sticky content to show
    stickyContent?: React.ReactNode;
    // If we hide the navigation arrows, instead when disabled.
    // Currently only affect desktopArrows
    hideNavDesktop?: boolean;
    // Sizes content container to parent, rather than content
    isSizedToParent?: boolean;
    contentClassName?: string;
}

const SCROLL_AMOUNT = 240;

const ContentSlider: React.FC<IContentSliderProps> = (props) => {
    const {
        className,
        stickyContent,
        hideNavDesktop,
        isSizedToParent,
        contentClassName,
        children
    } = props;

    const [validDirections, setValidDirections] = useState({ left: false, right: true });
    const [offSet, setOffSet] = useState({ left: 0, isHidden: true });

    const refContent = useRef<HTMLDivElement>(null);

    const calculateValidDirections = (): void => {
        const { scrollLeft, scrollWidth, offsetWidth } = refContent?.current || {};

        const boundingRect = refContent?.current.getBoundingClientRect();

        // Left hand visibility
        const canMoveLeft = scrollLeft > 0;
        // Right hand visibility
        const canMoveRight = scrollLeft < scrollWidth - offsetWidth;

        setOffSet({
            left: scrollLeft,
            isHidden:
                boundingRect.top > 0 ||
                boundingRect.bottom < refContent?.current.parentElement.offsetTop
        });

        setValidDirections({
            left: canMoveLeft,
            right: canMoveRight
        });
    };

    // We need to recalculate ideally when orientation changes.
    // https://developer.mozilla.org/en-US/docs/Web/API/Screen/orientation
    // Currently not supported by Safari (Accessed: 4th, May 2022)
    // One soluction is to instead use matchMedia
    // https://developer.mozilla.org/en-US/docs/Web/API/Window/matchMedia
    const isPortrait = window.matchMedia("(orientation: portrait)")?.matches;

    useEffect(() => {
        if (refContent?.current) calculateValidDirections();
        // refContent.current only changes from null (Initial value) to having a reference. It won't trigger additional effects.
        // https://stackoverflow.com/questions/60476155/is-it-safe-to-use-ref-current-as-useeffects-dependency-when-ref-points-to-a-dom
    }, [children, refContent?.current, isPortrait]);

    useEffect(() => {
        document.addEventListener("scroll", calculateValidDirections, { passive: true });
        document.body.addEventListener("scroll", calculateValidDirections, {
            passive: true
        });

        window.addEventListener("resize", calculateValidDirections);

        return () => {
            // Scroll event cleanup
            document.removeEventListener("scroll", calculateValidDirections);
            document.body.removeEventListener("scroll", calculateValidDirections);

            window.removeEventListener("resize", calculateValidDirections);
        };
    }, []);

    // https://developer.mozilla.org/en-US/docs/Web/API/Element/scrollBy
    // WebKit (Safari/iOS) doesn't support ScrollToOptions, so we can set the {behaviour: "smooth"} setting
    // This is set in the CSS instead: scroll-behavior: smooth;
    const moveLeft = (): void => {
        refContent.current.scrollBy(-SCROLL_AMOUNT, 0);
        calculateValidDirections();
    };

    const moveRight = (): void => {
        refContent.current.scrollBy(SCROLL_AMOUNT, 0);
        calculateValidDirections();
    };

    return (
        <div
            className={cx(styles["component_container"], "layout-flex-column", {
                [className]: !!className
            })}>
            <div className="layout-flex-row">
                <ContentSliderArrow
                    direction="left"
                    onClick={moveLeft}
                    isDisabled={!validDirections.left}
                    isNavHidden={hideNavDesktop}
                    className={cx(["hide-mobile"])}
                />

                <div className={cx(styles["slider"], "layout-flex-row")}>
                    {/* Content */}
                    <div
                        className={cx([
                            styles["slider__content"],
                            "hide-scrollbar",
                            {
                                [styles["slider__content-full-width"]]: isSizedToParent,
                                [contentClassName]: !!contentClassName
                            }
                        ])}
                        ref={refContent}
                        onScroll={calculateValidDirections}>
                        {/* Sticky Content */}
                        <div
                            className={cx([
                                styles["slider_sticky"],
                                {
                                    ["layout-hidden"]: offSet.isHidden
                                }
                            ])}
                            style={{
                                width: refContent?.current?.clientWidth || 0
                            }}>
                            <div
                                style={{
                                    transform: `translate(${-offSet.left}px)`
                                }}>
                                {stickyContent}
                            </div>
                        </div>

                        {children}
                    </div>
                </div>

                <ContentSliderArrow
                    direction="right"
                    onClick={moveRight}
                    isDisabled={!validDirections.right}
                    isNavHidden={hideNavDesktop}
                    className={cx(["hide-mobile"])}
                />
            </div>
        </div>
    );
};

export default ContentSlider;
